/**
 * Non-sensitive column statistics for optional Vertex mapping assistance.
 * Never includes original cell values (no owner names, no addresses).
 */

import { normalizeHeader } from "./prospectImportMapping.js";

const POSTAL_RE = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
const DATE_RE = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$|^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}$/;
const STREET_WORD_RE = /\b(rue|street|ave|avenue|blvd|boulevard|chemin|ch|route|rd|drive|dr|place|cres|way|lane)\b/i;

function ratio(count, total) {
  if (!total) return 0;
  return Math.round((count / total) * 100) / 100;
}

export function looksLikePostal(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  return POSTAL_RE.test(s.replace(/\s+/g, " "));
}

export function looksLikeAddress(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  if (looksLikePostal(s)) return false;
  const hasDigit = /\d/.test(s);
  const hasStreetWord = STREET_WORD_RE.test(s);
  return hasDigit && (hasStreetWord || s.length >= 8);
}

export function looksLikeNumber(value) {
  const s = String(value || "").trim().replace(/\s/g, "");
  if (!s) return false;
  return /^\d+([.,]\d+)?$/.test(s);
}

export function looksLikeDate(value) {
  const s = String(value || "").trim();
  if (!s) return false;
  return DATE_RE.test(s);
}

function profileColumnValues(values) {
  const list = Array.isArray(values) ? values : [];
  const total = list.length;
  let empty = 0;
  let addressLike = 0;
  let postalLike = 0;
  let numberLike = 0;
  let dateLike = 0;
  const unique = new Set();
  for (const raw of list) {
    const value = raw == null ? "" : String(raw).trim();
    if (!value) {
      empty += 1;
      continue;
    }
    unique.add(value.toLowerCase());
    if (looksLikePostal(value)) postalLike += 1;
    else if (looksLikeDate(value)) dateLike += 1;
    else if (looksLikeNumber(value)) numberLike += 1;
    if (looksLikeAddress(value)) addressLike += 1;
  }
  const nonEmpty = Math.max(0, total - empty);
  return {
    nonEmptyRatio: ratio(nonEmpty, total),
    uniqueRatio: ratio(unique.size, nonEmpty || 1),
    looksLikeAddressRatio: ratio(addressLike, total),
    looksLikePostalRatio: ratio(postalLike, total),
    looksLikeNumberRatio: ratio(numberLike, total),
    looksLikeDateRatio: ratio(dateLike, total),
  };
}

/**
 * @param {string[]} headers
 * @param {string[][]} rows
 * @returns {object[]}
 */
export function profileImportColumns(headers, rows) {
  const cols = Array.isArray(headers) ? headers : [];
  const data = Array.isArray(rows) ? rows : [];
  const occurrences = new Map();
  return cols.map((header, index) => {
    const normalizedHeader = normalizeHeader(header);
    const occKey = normalizedHeader || `col ${index}`;
    const occurrence = (occurrences.get(occKey) || 0) + 1;
    occurrences.set(occKey, occurrence);
    const values = data.map((row) => (Array.isArray(row) ? row[index] : ""));
    return {
      index,
      header: header == null ? "" : String(header),
      normalizedHeader,
      occurrence,
      dataProfile: profileColumnValues(values),
    };
  });
}

export function hasRepeatedAmbiguousHeaders(columns) {
  const list = Array.isArray(columns) ? columns : [];
  return list.some(
    (col) =>
      col.occurrence > 1 &&
      ["adresse", "ville", "code postal", "nom", "prenom"].includes(col.normalizedHeader),
  );
}

export function vertexMappingHelpful({ profile, mappingOk, columns, confidence }) {
  if (!mappingOk) return true;
  if (profile === "generic") return true;
  if (typeof confidence === "number" && confidence < 0.8) return true;
  if (hasRepeatedAmbiguousHeaders(columns)) return true;
  return false;
}
